<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { number, object, string } from 'yup';

const props = defineProps({
    package: {
        required: false,
        type: Object,
    },
    packageThemes: {
        type: Array,
        required: true,
    },
    packageFrequencies: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const form = reactive({
    theme: props.package
        ? props.packageThemes.find(
              (theme) => theme.value === props.package.theme.value,
          )
        : props.packageThemes[0],
    name: props.package ? props.package.name : '',
    description: props.package ? props.package.description : '',
    requirements: props.package ? props.package.requirements : '',
    price: props.package ? props.package.price : '',
    frequency:
        props.package && props.package.frequency
            ? props.packageFrequencies.find(
                  (frequency) =>
                      frequency.value === props.package.frequency.value,
              )
            : null,
    hoursPerMonth: props.package ? props.package.hoursPerMonth : '',
    netlinkingBudgetPerMonth:
        props.package && props.package.netlinkingBudgetPerMonth
            ? props.package.netlinkingBudgetPerMonth
            : null,
    minCost: props.package ? props.package.minCost : '',
    maxCost: props.package ? props.package.maxCost : '',
});

const isSubmitting = ref(false);

const validationSchema = object({
    theme: object().required('Le thème est requis'),
    name: string().required('Le nom est requis'),
    description: string().nullable(),
    requirements: string().nullable(),
    price: number().when('theme', {
        is: (theme) =>
            theme === 'seo' ||
            theme === 'sea' ||
            theme === 'smo' ||
            theme === 'sma' ||
            theme === 'sem' ||
            theme === 'hosting' ||
            theme === 'outsourcing' ||
            theme === 'cloudflare' ||
            theme === 'emailing' ||
            theme === 'shoppingfeed' ||
            theme === 'days' ||
            theme === 'special',
        then: number().required('Le prix est requis').min(0),
    }),
    frequency: object().nullable(),
    hoursPerMonth: number()
        .min(0)
        .when('theme', {
            is: (theme) =>
                theme === 'seo' ||
                theme === 'sea' ||
                theme === 'smo' ||
                theme === 'sma',
            then: number()
                .required("Le nombre d'heures par mois est requis")
                .min(0),
        }),
    netlinkingBudgetPerMonth: number().when('theme', {
        is: 'seo',
        then: number()
            .required('Le budget de netlinking par mois est requis')
            .min(0),
    }),
    minCost: number().when('theme', {
        is: (theme) => theme === 'sea' || theme === 'sma',
        then: number().required('Le coût minimum est requis').min(0),
    }),
    maxCost: number().when('theme', {
        is: (theme) => theme === 'sea' || theme === 'sma',
        then: number().required('Le coût maximum est requis').min(0),
    }),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.apikom.postPackage',
                props.package
                    ? {
                          package: props.package.id,
                      }
                    : {},
            ),
            {
                theme: form.theme.value,
                name: form.name,
                description: form.description,
                requirements: form.requirements,
                price: form.price,
                frequency: form.frequency ? form.frequency.value : null,
                hoursPerMonth: form.hoursPerMonth,
                netlinkingBudgetPerMonth: form.netlinkingBudgetPerMonth,
                minCost: form.minCost,
                maxCost: form.maxCost,
            },
        )
        .then((response) => {
            toastSaved();

            router.visit(
                route('front.projectManagement.apikom.package', {
                    package: response.data.id,
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(route('front.projectManagement.apikom.packages'));
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="2">
                    <ListboxField
                        v-model="form.theme"
                        field-id="theme"
                        field-name="theme"
                        label="Thème"
                        :options="props.packageThemes"
                        required
                    >
                        <template #buttonContent>
                            {{ form.theme.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        type="text"
                        required
                    />
                </FormGrid>

                <EditorField
                    v-model="form.description"
                    label="Description"
                    field-name="description"
                    field-id="description"
                    size="small"
                />

                <EditorField
                    v-model="form.requirements"
                    label="Besoin"
                    field-name="requirements"
                    field-id="requirements"
                    size="small"
                />
            </FormContent>

            <FormContent>
                <FormGrid>
                    <InputField
                        v-if="
                            form.theme.value === 'seo' ||
                            form.theme.value === 'sea' ||
                            form.theme.value === 'smo' ||
                            form.theme.value === 'sma' ||
                            form.theme.value === 'sem' ||
                            form.theme.value === 'dev' ||
                            form.theme.value === 'hosting' ||
                            form.theme.value === 'outsourcing' ||
                            form.theme.value === 'cloudflare' ||
                            form.theme.value === 'emailing' ||
                            form.theme.value === 'shoppingfeed' ||
                            form.theme.value === 'days' ||
                            form.theme.value === 'special'
                        "
                        v-model="form.price"
                        label="Prix"
                        field-name="price"
                        field-id="price"
                        type="number"
                        step="0.01"
                        min="0"
                        required
                    />
                </FormGrid>

                <FormGrid>
                    <ListboxField
                        v-model="form.frequency"
                        field-id="frequency"
                        field-name="frequency"
                        label="Fréquence"
                        :options="props.packageFrequencies"
                        has-empty-option
                    >
                        <template #buttonContent>
                            {{ form.frequency.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </FormGrid>

                <FormGrid>
                    <InputField
                        v-if="
                            form.theme.value === 'seo' ||
                            form.theme.value === 'sea' ||
                            form.theme.value === 'smo' ||
                            form.theme.value === 'sma' ||
                            form.theme.value === 'days'
                        "
                        v-model="form.hoursPerMonth"
                        label="Heures/mois"
                        field-name="hoursPerMonth"
                        field-id="hoursPerMonth"
                        type="number"
                        step="1"
                        min="0"
                        required
                    />

                    <InputField
                        v-if="form.theme.value === 'seo'"
                        v-model="form.netlinkingBudgetPerMonth"
                        label="Budget netlinking/mois"
                        field-name="netlinkingBudgetPerMonth"
                        field-id="netlinkingBudgetPerMonth"
                        type="number"
                        step="0.01"
                        min="0"
                        required
                    />
                </FormGrid>

                <FormGrid
                    v-if="
                        form.theme.value === 'sea' || form.theme.value === 'sma'
                    "
                >
                    <InputField
                        v-model="form.minCost"
                        label="Coût minimum"
                        field-name="minCost"
                        field-id="minCost"
                        type="number"
                        step="0.01"
                        min="0"
                        required
                    />

                    <InputField
                        v-model="form.maxCost"
                        label="Coût maximum"
                        field-name="maxCost"
                        field-id="maxCost"
                        type="number"
                        step="0.01"
                        min="0"
                        required
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="props.package"
                    :url="
                        route('front.projectManagement.apikom.deletePackage', {
                            package: props.package.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.apikom.packages',
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
